import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { CircleSlash2, FileText, Forward, Phone, Reply, Video } from 'lucide-react';
import { api, fileUrl, type ForwardView, type ReplyView } from '../api/client.ts';
import { useCall } from '../calls/CallContext.tsx';
import { canReplyTo, type MessageView } from '../chat/ChatContext.tsx';
import {
  formatCall,
  formatDaySeparator,
  formatFileSize,
  formatTime,
  isSameDay,
  msUntilNextDay,
  startOfDay,
} from '../lib/format.ts';
import { savesViaShell, saveViaShell } from '../lib/download.ts';
import { HOLD_MS } from '../lib/hold.ts';
import { isSoloSticker, parseMessage, smileyUrl } from '../lib/smileys.ts';
import { growSpan, wordSpanAt, type Span } from '../lib/selection.ts';
import { isArmed, replyProgress, replyPull, swipeAxis, type SwipeAxis } from '../lib/swipe.ts';
import { AlbumBubble } from './AlbumBubble.tsx';
import { Avatar } from './Avatar.tsx';
import { viewerForAlbum, type ViewerImage } from './ImageViewer.tsx';
import { VideoNoteBubble, VoiceBubble } from './MediaBubbles.tsx';
import { TextBody } from './TextBody.tsx';
import { TextSelection, indexAtPoint } from './TextSelection.tsx';
import './MessageThread.css';

/** How long after arrival a live message still reads as «только что». */
const JUST_NOW_MS = 60_000;

/**
 * How close to the top of the thread asking for the next page of history is
 * worth doing. Roughly a screenful ahead, so the messages are there by the time
 * the scroll reaches them and reading back never stops at a spinner.
 */
const LOAD_OLDER_PX = 600;

/**
 * How near the bottom counts as watching the conversation rather than reading
 * back through it — a couple of bubbles' worth.
 */
const STICK_PX = 160;

/**
 * How far from the very end still reads as «standing at the end». Not zero,
 * because scroll offsets are fractional under a non-integer device pixel
 * ratio; well under a line's height, because a whole line off the end is the
 * platform's stray scroll (see `stuck`), not a rounding difference.
 */
const PIN_EPS_PX = 8;

/**
 * How long a gesture's momentum is still its owner's after the finger lifts.
 * Fling scroll events arrive every frame, so each one extends the chain; a
 * scroll this long after the last of them was started by something else.
 */
const GESTURE_TAIL_MS = 150;

/**
 * Only a touchscreen's platform is known to scroll the thread uninvited, and
 * only a touchscreen delivers a pointer event for every scroll a person makes.
 * A desktop does neither — a scrollbar drag arrives with no pointerdown at
 * all — so there the reader's own scrolls must not be second-guessed.
 */
const COARSE_POINTER =
  typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;

/** Re-renders on a slow tick so «только что» turns into a clock time on its own. */
function useNow(enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, [enabled]);
  return now;
}

/**
 * Re-renders once at the next local midnight, and re-arms there.
 *
 * That is the only moment a day separator changes wording with nothing else
 * happening: «Сегодня» has to become «Вчера» over a conversation left open
 * overnight and untouched since. `useNow` above cannot do it — it only ticks
 * while the last message is a live incoming one, which a quiet thread's is not.
 * One timer a day beats polling for an event that happens on a schedule.
 */
function useDayBoundary(): void {
  const [day, setDay] = useState(() => startOfDay(new Date()));
  // Keyed on the day it is waiting out, so crossing one re-arms for the next
  // and an ordinary re-render leaves the pending timer alone.
  useEffect(() => {
    const timer = window.setTimeout(() => setDay(startOfDay(new Date())), msUntilNextDay());
    return () => window.clearTimeout(timer);
  }, [day]);
}

function MessageStatus({
  status,
  showTicks,
  onRetry,
}: {
  status: MessageView['status'];
  /** Whether ✓ / ✓✓ mean anything here — see MessageThread's own prop. */
  showTicks: boolean;
  onRetry: (() => void) | null;
}) {
  if (status === 'sending') {
    return (
      <>
        <span className="status-spinner" />
        отправка…
      </>
    );
  }
  /**
   * A failed send is a button now, and it has to be.
   *
   * The queue behind it outlives the page (chat/outbox.ts), so a send the
   * server refused outright — too big, or a chat that is gone — no longer
   * disappears when the app is reopened. It waits, with its file, until
   * somebody decides. Anything the network merely interrupted is retried
   * without being asked; this is for the rest.
   */
  if (status === 'failed') {
    return onRetry ? (
      <button type="button" className="status-failed status-retry" onClick={onRetry}>
        не отправлено · повторить
      </button>
    ) : (
      <span className="status-failed">не отправлено</span>
    );
  }
  // Nothing left to say in a chat with nobody else in it: «отправка» and «не
  // отправлено» above are facts about this device and its connection, and a ✓
  // is a fact about a reader who does not exist.
  if (!showTicks) return null;
  if (status === 'read') return <span className="status-check status-read">✓✓</span>;
  return <span className="status-check status-sent">✓</span>;
}

function MessageContent({
  message,
  highlight,
  picked,
  onOpenImage,
}: {
  message: MessageView;
  /** What the search field holds, marked inside the words of the bubble. */
  highlight?: string;
  /** The fragment being held in this message, painted inside its words. */
  picked?: Span | null;
  onOpenImage: (image: ViewerImage) => void;
}) {
  if (message.kind === 'text') {
    return <TextBody body={message.body ?? ''} highlight={highlight} selection={picked} />;
  }
  // The two recorded kinds are drawn by their own components, which the caller
  // renders directly — they are not bubble contents. See Message below.

  if (message.kind === 'image') {
    // While the bytes are still going up there is nothing to fetch yet, so the
    // bubble shows the placeholder block from the design.
    if (message.status === 'sending') {
      return <div className="message-image message-image-placeholder">изображение</div>;
    }
    return (
      <button
        type="button"
        className="message-image-link"
        onClick={() => onOpenImage({ id: message.id, name: message.fileName ?? null })}
        title={message.fileName ?? 'изображение'}
      >
        {/* The bubble is 180x120, so it loads the thumbnail; the full picture
            is fetched only when the viewer opens. */}
        <img
          className="message-image"
          src={fileUrl(message.id, { variant: 'thumb' })}
          alt={message.fileName ?? ''}
          loading="lazy"
          decoding="async"
        />
      </button>
    );
  }

  const file = (
    <>
      <div className="message-file-icon">
        <FileText size={16} />
      </div>
      <div className="message-file-meta">
        <div className="message-file-name">{message.fileName}</div>
        <div className="message-file-size">{formatFileSize(message.fileSize ?? 0)}</div>
      </div>
    </>
  );

  // The same row, minus the link: there is nothing on the server to fetch until
  // the upload finishes. It keeps the class so the row lays out identically —
  // and so a quote above it has one thing to sit on top of rather than two.
  if (message.status === 'sending') return <div className="message-file-link">{file}</div>;
  return (
    <a
      className="message-file-link"
      href={fileUrl(message.id)}
      download={message.fileName ?? ''}
      // Inside a shell the anchor is a no-op (see lib/download.ts), so the tap
      // goes through the share sheet — same arrangement as the viewer's button.
      onClick={(event) => {
        if (!savesViaShell) return;
        event.preventDefault();
        void saveViaShell(fileUrl(message.id), message.fileName ?? 'файл');
      }}
    >
      {file}
    </a>
  );
}

/**
 * The message a reply answers, above what the reply says about it.
 *
 * A button, because it goes to the original — the one thing anybody wants from
 * a quote. It is drawn from `replyTo`, which the server resolves on every read,
 * so an edited original shows its new text here and a deleted one says so.
 */
function ReplyQuote({ reply, onJump }: { reply: ReplyView; onJump: () => void }) {
  return (
    <button
      type="button"
      className={`reply-quote${reply.deleted ? ' reply-quote-gone' : ''}`}
      onClick={(event) => {
        // The bubble under it opens an image or plays a recording; the quote is
        // its own target and stops there.
        event.stopPropagation();
        onJump();
      }}
    >
      <span className="reply-quote-name">{reply.senderName}</span>
      <span className="reply-quote-text">
        {/* Text is quoted as its own body, codes and all, so a smiley in the
            original is a smiley here too. Everything else is a label.

            Without its links, though: the quote is a button that goes to the
            original, an anchor inside one is not something a browser will
            reliably open, and an address here is a label rather than an offer —
            the message it belongs to is one tap away and has the real link. */}
        {reply.kind === 'text' && !reply.deleted ? (
          <TextBody body={reply.preview} links={false} />
        ) : (
          reply.preview
        )}
      </span>
    </button>
  );
}

/**
 * Who wrote the words, on a message that was copied here from another chat.
 *
 * Not a button, unlike the quote above it: there is nowhere to go. The original
 * is in a conversation this reader is not in, which is also why the server
 * sends a name and nothing else — see forwardViewOf in server/src/chats.ts.
 */
function ForwardedFrom({ from }: { from: ForwardView }) {
  return (
    <span className="forwarded-from">
      <Forward size={13} />
      Переслано от {from.name}
    </span>
  );
}

/**
 * How far the finger has to travel, after the menu has opened under it, before
 * the hold is read as a drag through the words instead.
 *
 * The two gestures share their first 420 ms, which is what makes selecting feel
 * like a continuation rather than a second attempt: hold and let go for the
 * menu, hold and keep going to take the text. Far enough that the wobble of a
 * finger resting on glass is not a drag.
 */
const PICK_PX = 10;

/** How long a кружочек takes to grow. Kept in step with the transition on
    `.note` in MessageThread.css, which is what this is waiting for. */
const EXPAND_MS = 180;

/** How long a message stays lit after a quote has jumped the thread to it. */
/**
 * The assistant saying it cannot: a sentence with one way forward beside it.
 *
 * The one action is reporting. There used to be a «Повторить» here, and it was
 * the wrong offer: it re-sent the words above as a brand-new message, which
 * read as the chat duplicating itself — the same question twice, the second
 * copy unexplained. Asking again is what the composer is for, and the person's
 * own words are still right there to copy.
 *
 * The button reaches an admin, and there is exactly one — so it says his name.
 * Its state stays here rather than lifting: the answer is "this plate has been
 * reported", it is true of nothing else on the screen, and it does not need to
 * survive a scroll — the row it is about does.
 */
function NoticePlate({ message }: { message: MessageView }) {
  const [reported, setReported] = useState<'no' | 'sending' | 'done' | 'failed'>('no');

  const report = () => {
    if (reported !== 'no') return;
    setReported('sending');
    api
      .report('ai', message.id)
      .then(() => setReported('done'))
      // A report that could not be filed says so rather than pretending: this
      // button's whole worth is that pressing it did something.
      .catch(() => setReported('failed'));
  };

  return (
    <div className="thread-notice" data-message-id={message.id}>
      <span className="thread-notice-text">{message.body}</span>
      <span className="thread-notice-actions">
        <button
          type="button"
          className="thread-notice-action"
          onClick={report}
          disabled={reported !== 'no'}
        >
          {reported === 'done'
            ? 'Отправлено'
            : reported === 'failed'
              ? 'Не отправилось'
              : 'Отправить ошибку Олегу =)'}
        </button>
      </span>
    </div>
  );
}

const FLASH_MS = 1400;

function Message({
  message,
  justNow,
  senderName,
  senderAvatarId,
  showTicks,
  dimmed,
  cold,
  flashing,
  highlight,
  playing,
  expanded,
  canTranscribe,
  onOpenImage,
  onHold,
  picked,
  onPick,
  onPickCopy,
  onPickQuote,
  onReply,
  onJumpTo,
  onTogglePlay,
  onToggleExpand,
  onTranscribe,
  onEnded,
  onRetry,
  onRedraw,
}: {
  message: MessageView;
  justNow: boolean;
  /**
   * Who wrote it, over the bubble — set only in a group, and only on the first
   * message of a run, the way every chat app does it. `null` draws nothing.
   */
  senderName: string | null;
  /** Their avatar, drawn beside the same message the name is. */
  senderAvatarId: string | null;
  /** Whether a sent message gets a ✓ — see MessageThread's own prop. */
  showTicks: boolean;
  /** The message currently being rewritten fades back while it is edited. */
  dimmed: boolean;
  /**
   * Above the assistant's newest «/новый чат» — a message the bot no longer
   * reads. Drawn faded rather than hidden: it is still the conversation, and
   * hiding it would make the divider a deletion, which it is not.
   */
  cold: boolean;
  /** Just jumped to from a quote; lit for a moment so the eye finds it. */
  flashing: boolean;
  /** The open search query, or undefined when nothing is being searched for. */
  highlight?: string;
  /** Whether this is the voice message the thread is currently playing. */
  playing: boolean;
  /** Whether this is the кружочек the thread is currently showing enlarged. */
  expanded: boolean;
  /** Whether this deployment can turn a voice message into text at all. */
  canTranscribe: boolean;
  onOpenImage: (image: ViewerImage) => void;
  onHold: (message: MessageView) => void;
  /** The fragment being held inside this message, or null when it is not this one. */
  picked: Span | null;
  /** A drag through the words moved the fragment; null puts the handles away. */
  onPick: (message: MessageView, span: Span | null) => void;
  onPickCopy: () => void;
  onPickQuote: () => void;
  /** A completed swipe: this message is the one being answered. */
  onReply: (message: MessageView) => void;
  /** Its quote was tapped — go to the message it quotes. */
  onJumpTo: (messageId: string) => void;
  onTogglePlay: (message: MessageView) => void;
  onToggleExpand: (message: MessageView, node: HTMLElement | null) => void;
  /** «Аа» was pressed on a voice message with no text yet. */
  onTranscribe: (message: MessageView) => void;
  onEnded: (message: MessageView) => void;
  /** «Повторить» under a send the server refused. */
  onRetry: (clientId: string) => void;
  /** «Повторить в качестве» under a generated picture. */
  onRedraw: (image: NonNullable<MessageView['aiImage']>) => void;
}) {
  const side = message.fromMe ? 'out' : 'in';
  /**
   * «Повторить в качестве» has been pressed. One press is one paid generation,
   * and the two minutes it takes are exactly when a second press looks
   * tempting — so the button spends itself and says so, instead of quietly
   * queuing another picture.
   */
  const [redone, setRedone] = useState(false);
  // A smiley sent on its own is the message, so it drops the bubble and is
  // drawn large — a sticker, the way every chat app treats one.
  const solo =
    message.kind === 'text' && message.body !== null && isSoloSticker(parseMessage(message.body));

  /**
   * Whether the menu opened out from under the finger that is still down.
   *
   * A recording is the one message you can both hold and tap: holding opens the
   * menu, tapping plays a voice message or resizes a кружочек. Without this,
   * holding a кружочек would open the menu and blow the circle up behind it,
   * because the click that follows the press arrives all the same. Cleared on
   * the next press.
   */
  const heldOpen = useRef(false);
  // Handed to the thread on a tap, which scrolls it back into view once it has
  // finished growing.
  const noteRef = useRef<HTMLDivElement>(null);
  // Only a call bubble uses it, and only to place the same call again.
  const { callBack } = useCall();
  const timer = useRef<number | null>(null);
  const cancelHold = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  };
  // Cleared on unmount as well: a message deleted while a finger is on it would
  // otherwise open a menu for something that is no longer there.
  useEffect(() => cancelHold, []);

  /** The words of this message, which is the only thing a selection can be in. */
  const textRef = useRef<HTMLSpanElement>(null);
  const selectable = message.kind === 'text' && !solo && (message.body ?? '') !== '';
  /** Where the finger went down, so the drag that follows can be measured. */
  const holdAt = useRef<{ x: number; y: number } | null>(null);
  /** The word the drag started from; both ends grow away from it. */
  const pickFrom = useRef<Span | null>(null);

  /**
   * Watches the finger that has just opened the menu, in case it keeps going.
   *
   * On the window rather than on the bubble: the menu is a full-screen overlay
   * and it is now between the finger and the message. touchmove is blocked for
   * the same span of time — the thread is free to pan vertically
   * (`touch-action: pan-y`) and would otherwise scroll the words out from under
   * the gesture rather than let it select them.
   */
  const watchForPick = () => {
    const from = holdAt.current;
    const body = message.body;
    if (!selectable || !from || !body) return;

    const block = (event: TouchEvent) => event.preventDefault();
    const move = (event: PointerEvent) => {
      const root = textRef.current;
      if (!root) return;

      if (pickFrom.current === null) {
        if (Math.hypot(event.clientX - from.x, event.clientY - from.y) < PICK_PX) return;
        const at = indexAtPoint(root, body, from.x, from.y);
        if (at === null) return;
        pickFrom.current = wordSpanAt(body, at);
        onPick(message, pickFrom.current);
      }

      const index = indexAtPoint(root, body, event.clientX, event.clientY);
      if (index !== null) onPick(message, growSpan(body, pickFrom.current, index));
    };
    const stop = () => {
      pickFrom.current = null;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      window.removeEventListener('touchmove', block);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    window.addEventListener('touchmove', block, { passive: false });
  };

  /**
   * Swipe left to reply, as in Telegram.
   *
   * Touch only. A press-and-drag with a mouse is not a gesture anyone makes at
   * a chat message — it is how text gets selected — so on a desktop the same
   * verb lives in the menu the right button opens.
   */
  /** How far the message has followed the finger. */
  const [pull, setPull] = useState(0);
  /** The same number where pointerup can read what the last move wrote, rather
      than what the last render closed over. */
  const pullRef = useRef(0);
  /** Set while a swipe is in progress, so the message tracks the finger exactly. */
  const [dragging, setDragging] = useState(false);
  /** Where the finger went down and which way it turned out to be going. */
  const swipe = useRef<{ x: number; y: number; axis: SwipeAxis } | null>(null);
  /** A swipe that armed swallows the click that its release produces. */
  const replied = useRef(false);

  const movePull = (next: number) => {
    // The buzz is the answer to "is it far enough yet", so it happens the
    // moment it becomes true rather than when the finger comes off.
    if (isArmed(next) && !isArmed(pullRef.current)) navigator.vibrate?.(10);
    pullRef.current = next;
    setPull(next);
  };

  const endSwipe = () => {
    swipe.current = null;
    setDragging(false);
    movePull(0);
  };

  const swiping = {
    onPointerDown: (event: ReactPointerEvent) => {
      replied.current = false;
      if (event.pointerType !== 'touch' || !canReplyTo(message)) return;
      swipe.current = { x: event.clientX, y: event.clientY, axis: 'none' };
    },
    onPointerMove: (event: ReactPointerEvent) => {
      const start = swipe.current;
      if (!start) return;

      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (start.axis === 'none') start.axis = swipeAxis(dx, dy);
      // Scrolling the thread is not a half-hearted reply: once the gesture is
      // vertical it stays that way until the finger comes off.
      if (start.axis === 'vertical') {
        endSwipe();
        return;
      }
      if (start.axis === 'horizontal') {
        setDragging(true);
        movePull(replyPull(dx));
      }
    },
    onPointerUp: () => {
      if (isArmed(pullRef.current)) {
        replied.current = true;
        onReply(message);
      }
      endSwipe();
    },
    // Fired when the browser takes the gesture over for its own scrolling,
    // which `touch-action: pan-y` leaves it free to do.
    onPointerCancel: endSwipe,
    // The click a release produces belongs to the swipe, not to the image or
    // the кружочек it happened to land on. Capture, so it never reaches them.
    onClickCapture: (event: MouseEvent) => {
      if (!replied.current) return;
      event.preventDefault();
      event.stopPropagation();
      replied.current = false;
    },
  };

  const held = {
    onPointerDown: (event: ReactPointerEvent) => {
      cancelHold();
      heldOpen.current = false;
      holdAt.current = { x: event.clientX, y: event.clientY };
      // A mouse does not have to wait out the hold. Dragging across words is
      // what selecting text has always been with a pointer, and the first move
      // cancels the timer below, so the menu never appears mid-drag — it is
      // still there for the press that stays put and for the right button.
      if (event.pointerType === 'mouse') watchForPick();
      timer.current = window.setTimeout(() => {
        timer.current = null;
        heldOpen.current = true;
        navigator.vibrate?.(10);
        onHold(message);
        watchForPick();
      }, HOLD_MS);
    },
    // The click that follows a press-and-hold belongs to the menu that just
    // opened, not to whatever it landed on.
    onClickCapture: (event: MouseEvent) => {
      if (!heldOpen.current) return;
      event.preventDefault();
      event.stopPropagation();
    },
    onPointerUp: cancelHold,
    onPointerLeave: cancelHold,
    // A scroll started on top of a message is not a press.
    onPointerCancel: cancelHold,
    onPointerMove: cancelHold,
    // The same menu on a desktop, where nobody holds a mouse button down.
    onContextMenu: (event: MouseEvent) => {
      event.preventDefault();
      cancelHold();
      onHold(message);
    },
  };

  const content = (
    <MessageContent
      message={message}
      highlight={highlight}
      picked={picked}
      onOpenImage={onOpenImage}
    />
  );

  const quote = message.replyTo && (
    <ReplyQuote reply={message.replyTo} onJump={() => onJumpTo(message.replyTo!.id)} />
  );
  // Above the quote wherever both could appear, though in practice they never
  // do: a forward carries no reply, because the message it would be answering
  // is in a conversation the reader of the copy cannot open.
  const forwarded = message.forwardedFrom && <ForwardedFrom from={message.forwardedFrom} />;
  // Inside the bubble on the three kinds that have one to put it in. A кружочек
  // and a sticker have no bubble, and a voice bubble is a row built around its
  // waveform — all three carry the quote above themselves instead. So does an
  // album: its wrapper clips everything inside it to the bubble's corners, and a
  // quote in there would be clipped along with the grid.
  const quoteInBubble =
    !solo && (message.kind === 'text' || message.kind === 'image' || message.kind === 'file');

  return (
    <div
      // Read by the thread to keep the reader's place when a page of history
      // lands above this message.
      data-message-id={message.id}
      className={`message message-${side}${dimmed ? ' message-dimmed' : ''}${
        cold ? ' message-cold' : ''
      }${flashing ? ' message-flash' : ''}${dragging ? ' message-dragging' : ''}${
        // An enlarged кружочек is wider than the column every other message
        // lives in, so the row it sits in stops constraining it.
        expanded ? ' message-wide' : ''
      }`}
      style={pull > 0 ? { transform: `translateX(${-pull}px)` } : undefined}
      {...swiping}
    >
      {/* Comes in from beyond the right edge of the thread as the message
          leaves it, and lights up when letting go would reply. */}
      {pull > 0 && (
        <span
          className={`message-reply-hint${isArmed(pull) ? ' message-reply-hint-armed' : ''}`}
          style={{
            opacity: replyProgress(pull),
            transform: `translateY(-50%) scale(${0.6 + 0.4 * replyProgress(pull)})`,
          }}
          aria-hidden="true"
        >
          <Reply size={17} />
        </span>
      )}

      {/* The avatar sits in the gutter beside the message rather than in a row
          with it: .message is a column every kind of bubble — a кружочек, an
          album, a sticker — already lays itself out inside, and an absolutely
          placed tile leaves all of that alone. It rides along with the swipe
          for free, since the swipe moves .message itself. */}
      {senderName !== null && (
        <>
          <Avatar className="message-avatar" photoId={senderAvatarId} name={senderName} />
          <div className="message-sender">{senderName}</div>
        </>
      )}

      {!quoteInBubble && forwarded}
      {!quoteInBubble && quote}

      {/* A кружочек is a circle, not a bubble — the same way a lone smiley is
          a sticker rather than a bubble with a picture in it. */}
      {message.kind === 'videoNote' ? (
        <div className="message-note" ref={noteRef} {...held}>
          <VideoNoteBubble
            message={message}
            expanded={expanded}
            onToggleExpand={() => onToggleExpand(message, noteRef.current)}
          />
        </div>
      ) : message.kind === 'album' ? (
        // Not a bubble with pictures in it: the grid *is* the bubble, wearing
        // the same corners and clipping the caption bar to them.
        <div className="message-album" {...held}>
          <AlbumBubble
            message={message}
            // Nothing to open while the bytes are still going up — the tiles are
            // this device's own files, and the viewer fetches from the server.
            onOpen={(index) => {
              if (message.status !== 'sending') onOpenImage(viewerForAlbum(message, index));
            }}
          />
        </div>
      ) : message.kind === 'voice' ? (
        <div className={`bubble bubble-${side} bubble-voice`} {...held}>
          <VoiceBubble
            message={message}
            playing={playing}
            onToggle={() => onTogglePlay(message)}
            onEnded={() => onEnded(message)}
            canTranscribe={canTranscribe}
            onTranscribe={() => onTranscribe(message)}
          />
        </div>
      ) : message.kind === 'call' ? (
        // An ordinary bubble in shape and colour, but a button: every variant
        // of it offers to place the same call again, which is the one thing
        // anyone wants from a call in their history.
        <button
          type="button"
          className={`bubble bubble-${side} bubble-call${
            message.callResult !== 'ended' ? ' bubble-call-failed' : ''
          }`}
          onClick={() => callBack(message.conversationId, message.callVideo ?? false)}
          {...held}
        >
          <span className="bubble-call-chip">
            {message.callVideo ? <Video size={15} /> : <Phone size={15} />}
          </span>
          <span className="bubble-call-text">
            <span className="bubble-call-title">{formatCall(message)}</span>
            <span className="bubble-call-hint">
              {message.callResult === 'failed'
                ? 'Нажмите, чтобы повторить'
                : 'Нажмите, чтобы перезвонить'}
            </span>
          </span>
        </button>
      ) : solo ? (
        <div className="message-sticker" {...held}>
          {content}
        </div>
      ) : (
        <div className={`bubble bubble-${side} bubble-${message.kind}`} {...held}>
          {forwarded}
          {quote}
          {/* Words a button re-sent, saying so. Without the label this bubble
              is an unexplained copy of one's own message — which is exactly
              what got reported as the chat duplicating itself. */}
          {message.aiNotice === 'redo' && (
            <span className="bubble-redo">повтор в максимальном качестве</span>
          )}
          {/* The words get a wrapper of their own so a selection has something to
              measure and hit-test that holds the message and not the quote above
              it — which is drawn by the same component and would otherwise offer
              its own letters to the arithmetic. */}
          {selectable ? (
            <span className="bubble-words" ref={textRef}>
              {content}
            </span>
          ) : (
            content
          )}
        </div>
      )}

      {picked && message.body !== null && (
        <TextSelection
          root={textRef.current}
          body={message.body}
          span={picked}
          onChange={(span) => onPick(message, span)}
          onCopy={onPickCopy}
          onQuote={onPickQuote}
          onClose={() => onPick(message, null)}
        />
      )}

      {message.reactions.length > 0 && (
        <button
          type="button"
          className="message-reactions"
          onClick={() => onHold(message)}
          aria-label="Реакция"
        >
          {message.reactions.map((reaction) => (
            <span key={reaction.slug} className="message-reaction">
              <img src={smileyUrl(reaction.slug)} alt={reaction.slug} />
              {/* A count of one is the normal case in a chat of two, and
                  writing «1» beside every chip says nothing. */}
              {reaction.count > 1 && <span>{reaction.count}</span>}
            </span>
          ))}
        </button>
      )}

      {justNow ? (
        <div className="message-meta">
          <span className="just-now-dot" />
          только что
        </div>
      ) : (
        <div className="message-meta">
          {formatTime(message.createdAt)}
          {message.editedAt !== null && <span>· изменено</span>}
          {message.fromMe && (
            <MessageStatus
              status={message.status}
              showTicks={showTicks}
              onRetry={message.status === 'failed' ? () => onRetry(message.id) : null}
            />
          )}
        </div>
      )}

      {/* Under the picture rather than in the meta row above: this says which
          attempt it is and offers the two things anybody does next, and both of
          those belong to the picture rather than to the message that carried
          it. Only ever present on the assistant's own output. */}
      {message.aiImage && (
        <div className="message-version">
          <div className="message-version-line">
            <b>версия {message.aiImage.version}</b>
            <span>
              {message.aiImage.edited ? 'правка' : 'новая картинка'} ·{' '}
              {RATIO_LABELS[message.aiImage.size] ?? message.aiImage.size} ·{' '}
              {QUALITY_LABELS[message.aiImage.quality] ?? message.aiImage.quality}
            </span>
          </div>
          <div className="message-version-actions">
            {/* Answering it, which is exactly what makes it the next edit's
                subject — the same gesture as swiping the bubble, said with a
                word for anybody who has not found the swipe. */}
            <button
              type="button"
              className="message-version-action"
              onClick={() => onReply(message)}
            >
              <Reply size={15} />
              Доработать
            </button>
            {/* Only where it would change anything. On a picture already drawn
                at the top tier the button would be an offer to spend the same
                money on the same request. */}
            {message.aiImage.quality !== 'high' && (
              <button
                type="button"
                className="message-version-action"
                disabled={redone}
                onClick={() => {
                  setRedone(true);
                  onRedraw(message.aiImage!);
                }}
              >
                {redone ? 'Отправлено' : 'Повторить в качестве'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** The shapes the sizes stand for, as the composer names them. */
const RATIO_LABELS: Record<string, string> = {
  '1024x1024': '1:1',
  '1024x1536': '2:3',
  '1536x1024': '3:2',
};

const QUALITY_LABELS: Record<string, string> = {
  low: 'черновик',
  medium: 'хорошее',
  high: 'максимум',
};

/**
 * Whether this message opens a run from one person — which is where the name
 * and the avatar go. Repeating either over every bubble of a paragraph typed in
 * three goes is noise, and one's own messages need neither.
 */
function startsRun(messages: MessageView[], index: number, showSenders: boolean): boolean {
  const message = messages[index]!;
  if (!showSenders || message.fromMe) return false;
  return messages[index - 1]?.senderId !== message.senderId;
}

export function MessageThread({
  messages,
  showSenders,
  showTicks,
  editingId,
  hasMore,
  loadingOlder,
  onLoadOlder,
  hasNewer = false,
  onLoadNewer,
  focus = null,
  highlight,
  onOpenImage,
  onHold,
  picked,
  onPick,
  onPickCopy,
  onPickQuote,
  onReply,
  onViewed,
  canTranscribe,
  onTranscribe,
  onRetry,
  onRedraw,
}: {
  messages: MessageView[];
  /** True in a group, where an incoming bubble has to say who wrote it. */
  showSenders: boolean;
  /**
   * False in «Заметки», where a receipt has no one to come from. Every other
   * chat has somebody on the other end, and a ✓ turning into ✓✓ is the whole
   * point of drawing one.
   */
  showTicks: boolean;
  editingId: string | null;
  /** Whether there is history before the first message here. */
  hasMore: boolean;
  loadingOlder: boolean;
  /** Asked for whenever the reader gets near the top; ignores what it must. */
  onLoadOlder: () => void;
  /**
   * Whether this is a window — the thread stops in the middle of the
   * conversation rather than at the end of it. Only a jump to a search result
   * opens one; see focusMessage in ChatContext.
   */
  hasNewer?: boolean;
  /** The mirror of onLoadOlder at the other end, and only ever in a window. */
  onLoadNewer?: () => void;
  /**
   * A message to go to and light up, named from outside — a tapped search
   * result. The timestamp is what makes the same message a new instruction the
   * second time it is tapped.
   */
  focus?: { id: string; at: number } | null;
  /** The open search query, marked inside every bubble that contains it. */
  highlight?: string;
  onOpenImage: (image: ViewerImage) => void;
  onHold: (message: MessageView) => void;
  /** The message a fragment is being held in, and where in it. */
  picked: { id: string; span: Span } | null;
  onPick: (message: MessageView, span: Span | null) => void;
  onPickCopy: () => void;
  onPickQuote: () => void;
  /** A message swiped left: the composer takes it as what is being answered. */
  onReply: (message: MessageView) => void;
  /** The first play of an incoming кружочек; greys its ring for good. */
  onViewed: (messageId: string) => void;
  /** Whether this deployment has a speech engine behind it. */
  canTranscribe: boolean;
  /** «Аа» on a voice message: have the server turn it into text. */
  onTranscribe: (messageId: string) => void;
  /** «Повторить» on a send the server refused; the id is the sender's own. */
  onRetry: (clientId: string) => void;
  /**
   * «Повторить в качестве»: the same picture request at the top tier. Sent as
   * an ordinary message replying to whatever the original was made from, so it
   * is the same operation rather than a new one wearing the same words.
   */
  onRedraw: (image: NonNullable<MessageView['aiImage']>) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const last = messages.at(-1);
  /**
   * The newest memory boundary among the messages that are loaded, and -1 where
   * there is none — which is every chat but the assistant's.
   *
   * Everything before it is drawn cold. An older divider higher up in the
   * history stays a divider, but only the newest one carries the explanation:
   * the sentence is about what the bot remembers now, and repeating it at every
   * old break would turn it into wallpaper.
   */
  const lastResetIndex = messages.findLastIndex((item) => item.aiNotice === 'reset');
  const lastIsLive = last !== undefined && !last.fromMe && last.live === true;
  const now = useNow(lastIsLive);
  useDayBoundary();

  /**
   * Sticking to the bottom is for messages arriving, and nothing else.
   *
   * Watching the array itself was fine while the only thing that replaced it
   * was a new message or a receipt. Reactions, edits and deletes rewrite it
   * too, and being yanked to the newest message because someone put a smiley
   * on an old one is exactly how you lose your place in a conversation you
   * were reading. So the cases worth scrolling for are named instead: a
   * different conversation, a longer thread, or the same length with a new
   * last message — which is an optimistic bubble becoming a real one.
   *
   * A page of history is the case that looks like all of those and is none of
   * them: the thread gets longer, but at the far end. It is told apart by the
   * first id changing while the last one stays put.
   */
  const conversationId = messages[0]?.conversationId ?? null;
  const firstId = messages[0]?.id ?? null;
  const lastId = last?.id ?? null;
  const count = messages.length;
  const seen = useRef({ conversationId, firstId, lastId: null as string | null, count: 0 });
  /**
   * The thread's height as of the previous commit — the one number the
   * arrival-while-reading case below still needs, because it is the only case
   * reverse geometry does not hold by itself: the end moves away, and with it
   * the reader's distance-to-end-anchored line.
   */
  const height = useRef(0);

  /**
   * Which voice message is playing, held by the thread rather than by each
   * bubble: a second one starting has to stop the first, because nobody wants
   * two people talking at once.
   */
  const [playingId, setPlayingId] = useState<string | null>(null);

  /**
   * Which кружочек is enlarged — and therefore the one with sound, since the
   * two are one gesture (see VideoNoteBubble). One at a time, which is now the
   * same rule as above rather than merely a tidiness one: every other note on
   * screen is still running, but muted, so there is only ever one voice.
   */
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const togglePlay = useCallback(
    (message: MessageView) => {
      const starting = playingId !== message.id;
      setPlayingId(starting ? message.id : null);
      // A voice message starting shrinks whatever кружочек was talking. The two
      // kinds are the same claim on the reader's ears, so they queue together.
      if (starting) setExpandedId(null);
    },
    [playingId],
  );

  const toggleExpand = useCallback(
    (message: MessageView, node: HTMLElement | null) => {
      const opening = expandedId !== message.id;
      setExpandedId(opening ? message.id : null);
      if (!opening) return;

      // …and the other way round: enlarging a кружочек stops the voice message.
      setPlayingId(null);

      // Watched on the first tap. Playing it is not the signal any more — every
      // кружочек on screen plays — and scrolling past one is not watching it.
      if (!message.fromMe && message.viewedAt === null) onViewed(message.id);

      // A circle enlarged near the bottom of the thread would otherwise grow
      // off the end of it. Waited out rather than done immediately, so the
      // scroll lands on the size it is going to be.
      if (node)
        window.setTimeout(() => {
          // Same statement as in jumpTo: this scroll is meant, so the reader
          // is no longer the thread's to hold at the end.
          stuck.current = false;
          node.scrollIntoView({ block: 'nearest' });
        }, EXPAND_MS);
    },
    [expandedId, onViewed],
  );

  /** Nothing follows a finished voice message; it just stops being the one playing. */
  const advance = useCallback(() => setPlayingId(null), []);

  /**
   * The message a quote was tapped on, and when — the timestamp is what lets the
   * same quote be tapped twice and light its target up both times.
   */
  const [flash, setFlash] = useState<{ id: string; at: number } | null>(null);

  const jumpTo = useCallback((messageId: string) => {
    const node = scrollRef.current?.querySelector<HTMLElement>(
      `[data-message-id="${CSS.escape(messageId)}"]`,
    );
    // A quote can point further back than the pages loaded so far, and one that
    // leads nowhere stays put rather than jumping somewhere wrong. The search
    // screen does fetch: it loads a window around the message first (see
    // focusMessage in ChatContext) and only then asks for this jump, which is
    // why the answer has to say whether there was anything to jump to.
    if (!node) return false;
    // Following a quote is leaving the bottom on purpose; said out loud, or
    // the stray-scroll watch in onScroll would drag the reader straight back.
    stuck.current = false;
    node.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setFlash({ id: messageId, at: Date.now() });
    return true;
  }, []);

  /**
   * Which jump has already been made. A jump asked for before its window has
   * arrived waits for the messages to change and tries again; one that has
   * landed must not be repeated when a page of history arrives later and
   * hauls the reader back to a message they have moved on from.
   */
  const jumped = useRef(0);
  const focusPending = focus !== null && jumped.current !== focus.at;

  useEffect(() => {
    if (focus === null || jumped.current === focus.at) return;
    if (jumpTo(focus.id)) jumped.current = focus.at;
  }, [focus, messages, jumpTo]);

  // The highlight is a moment's help finding the message, not a selection.
  useEffect(() => {
    if (!flash) return;
    const timer = window.setTimeout(() => setFlash(null), FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [flash]);

  // Leaving the conversation stops whatever was playing in it — otherwise the
  // sound carries into the next thread, where nothing on screen explains it —
  // and drops the enlarged circle, which belonged to the thread being left.
  useEffect(() => {
    setPlayingId(null);
    setExpandedId(null);
  }, [conversationId]);

  /**
   * Whether the reader is standing at the end of the thread.
   *
   * There is remarkably little left for this bit to do, and that is the
   * design. The thread lays out `column-reverse` (see MessageThread.css), so
   * the end of the conversation is scrollTop 0 and history is scrolled into
   * at negative offsets. Standing at the end is the scroller's resting state:
   * the composer growing a line, the keyboard rising, a photo landing in the
   * newest bubble — none of it needs a correcting scroll from here, because
   * zero is still zero when the box resizes. The machinery this replaces kept
   * the bottom by writing scrollTop after every resize, and on both mobile
   * WebViews the platform would sometimes take that write back one layout
   * later — no touch, no event, just the correction quietly erased and the
   * thread left a line short of its end. A correction that is never issued
   * cannot be erased; what the browser keeps on its own, nothing fights.
   *
   * So the bit only answers the one question geometry cannot: should a new
   * message pull the thread down to itself. It changes hands deliberately —
   * the reader's own scroll moves it (see onScroll), and the two jumps that
   * leave the end on purpose, following a quote and enlarging a кружочек,
   * drop it before they scroll.
   */
  const stuck = useRef(true);
  /** To the end of the thread, which in this geometry is scrollTop zero. */
  const pin = useCallback((node: HTMLDivElement) => {
    node.scrollTop = 0;
    stuck.current = true;
  }, []);

  // Before paint, so an arrival the rule below decides to follow is followed
  // in the same frame it renders.
  useLayoutEffect(() => {
    const node = scrollRef.current;
    if (!node) return;

    const previous = seen.current;
    const previousHeight = height.current;
    seen.current = { conversationId, firstId, lastId, count };
    height.current = node.scrollHeight;

    // A window that has just replaced the thread looks from here exactly like a
    // thread that grew, and the rule below would send it to the bottom — which
    // is the opposite end from the message it was opened for. The jump owns the
    // scroll position until it has been made.
    if (focusPending) return;

    if (conversationId !== previous.conversationId) {
      pin(node);
      return;
    }

    // A page of history, or a reconnect trimming the thread back to one fetch:
    // the thread changed at its far end only. In reverse geometry that end
    // grows and shrinks past the edge of the visible without moving it — the
    // reader's offset is measured from the end they are reading towards — so
    // there is nothing to put back, and no anchor to have remembered.
    if (firstId !== previous.firstId && lastId === previous.lastId) return;

    if (count === previous.count && lastId === previous.lastId) return;

    // A held fragment is a place in the text with a finger on it. Going to the
    // newest message would take the words out from under the gesture, so an
    // arrival waits until the handles are put away.
    if (picked) return;

    // Reading history is not interrupted by somebody else's message: the thread
    // follows the newest bubble only if the newest bubble was already in view.
    // One's own is always worth going to — you just wrote it. Distance from
    // the end is simply -scrollTop here — the new bubble renders in this very
    // frame, and the browser holds the offset steady while it does.
    const wasNearBottom = -node.scrollTop < STICK_PX;
    if (stuck.current || wasNearBottom || last?.fromMe) {
      pin(node);
      return;
    }

    // A reader up in the history measures their place from the end of the
    // thread — that is what scrollTop is in this geometry — and a message
    // landing at that end just moved it away from under them. Hold the line
    // they were reading by growing their distance by exactly what arrived.
    if (lastId !== previous.lastId && count > previous.count) {
      node.scrollTop -= node.scrollHeight - previousHeight;
    }
  }, [conversationId, firstId, lastId, count, last?.fromMe, picked, focusPending, pin]);

  // The keyboard rising, the composer growing a line, a reply banner, a photo
  // landing in the newest bubble: none of it is corrected for any more, and
  // that absence is the design. In reverse geometry the browser keeps a reader
  // at scrollTop 0 on the end of the thread through every one of those, and
  // keeps an unstuck reader's distance-from-the-end — which pins what they are
  // reading to the edge the composer takes pixels from and gives them back to.
  // The ResizeObservers that used to do this by hand are gone with the model
  // that needed them; see `stuck` for why their writes could not be trusted.

  /**
   * The lock screen is the one move nobody reports. While the app is hidden
   * the platform is free to shift scrollTop with no observable event on this
   * side — a fling still decelerating when the screen went dark, the WebView
   * shuffling its viewport on the way back. Returning to the foreground
   * re-asks the one question everything here reduces to: was the reader at
   * the end of the thread? If they were, that is where they come back to,
   * whatever the lock screen did to the offset. A frame is waited out first
   * so the write lands in the layout the WebView actually resumed with, not
   * one it is mid-way through reassembling.
   */
  useEffect(() => {
    let frame = 0;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      frame = requestAnimationFrame(() => {
        const node = scrollRef.current;
        if (!node || picked || focusPending) return;
        if (stuck.current) pin(node);
      });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      cancelAnimationFrame(frame);
    };
  }, [picked, focusPending, pin]);

  const reachOlder = useCallback(() => {
    const node = scrollRef.current;
    if (!node || !hasMore || loadingOlder) return;
    // scrollTop runs from -(scrollHeight - clientHeight) at the top of history
    // to 0 at the end of the thread, so this sum is the distance left to the
    // top — the same screenful of lead the constant was chosen for.
    if (node.scrollHeight - node.clientHeight + node.scrollTop <= LOAD_OLDER_PX) onLoadOlder();
  }, [hasMore, loadingOlder, onLoadOlder]);

  /**
   * The same at the bottom, and only ever inside a window: an ordinary thread
   * ends where the conversation does, and there is nothing under it to fetch.
   * Walking down out of a window is what closes it — see loadNewerMessages.
   */
  const reachNewer = useCallback(() => {
    const node = scrollRef.current;
    if (!node || !hasNewer) return;
    if (-node.scrollTop <= LOAD_OLDER_PX) onLoadNewer?.();
  }, [hasNewer, onLoadNewer]);

  /**
   * Whose hand is on the scroller. A pointer held down is the reader; so is
   * the momentum after it lifts, recognised as a chain of scroll events each
   * arriving within GESTURE_TAIL_MS of the one before, begun while the
   * pointer was down. A wheel counts the same way. Everything else — this
   * component's own corrections, the browser's clamps, and the WebView's
   * uninvited line — arrives outside any gesture, which is what lets onScroll
   * tell the reader changing their place from the place being changed under
   * them. The on-screen keyboard is a system window, so typing produces no
   * pointer events here — exactly why a keystroke's stray scroll is
   * recognisable at all.
   */
  const gesture = useRef({ held: false, tail: 0 });
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const down = () => {
      gesture.current.held = true;
    };
    const up = () => {
      gesture.current.held = false;
      gesture.current.tail = performance.now() + GESTURE_TAIL_MS;
    };
    const spin = () => {
      gesture.current.tail = performance.now() + GESTURE_TAIL_MS;
    };
    node.addEventListener('pointerdown', down, { passive: true });
    node.addEventListener('wheel', spin, { passive: true });
    window.addEventListener('pointerup', up, { passive: true });
    window.addEventListener('pointercancel', up, { passive: true });
    return () => {
      node.removeEventListener('pointerdown', down);
      node.removeEventListener('wheel', spin);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, []);

  // Both halves of what a scroll means here: where the reader is, and whether
  // that is close enough to either end to be worth fetching what is past it.
  const onScroll = useCallback(() => {
    const node = scrollRef.current;
    if (!node) return;
    // A scroll that is nobody's — and both mobile WebViews produce them — is
    // deliberately left alone: pushing back from a scroll event paints a
    // second jerk after the already-painted first (the screen scrolls on the
    // compositor thread, ahead of this handler), and refusing to adopt it into
    // `stuck` is all the defence the model needs.
    const own =
      !COARSE_POINTER || gesture.current.held || performance.now() < gesture.current.tail;
    if (own) {
      // Only the reader's own scroll may take the reader off the end — or put
      // them back on it. Each momentum event extends the gesture's chain.
      gesture.current.tail = performance.now() + GESTURE_TAIL_MS;
      stuck.current = -node.scrollTop < PIN_EPS_PX;
    }
    reachOlder();
    reachNewer();
  }, [reachOlder, reachNewer]);

  // A thread shorter than its own viewport never fires a scroll event, so the
  // first screenful has to ask for the next page itself — and go on asking
  // while the pages keep failing to fill the window.
  useEffect(reachOlder, [reachOlder, count]);
  useEffect(reachNewer, [reachNewer, count]);

  return (
    // Transparent over the wallpaper, which ChatScreen paints on the wrapper
    // behind both this and the composer — the composer floats over the same
    // background, and a background painted here would end at the seam.
    <div
      // The group modifier is what opens the left gutter the avatars sit in;
      // a direct chat has no second face to draw and keeps the full width.
      className={showSenders ? 'thread thread-group' : 'thread'}
      ref={scrollRef}
      onScroll={onScroll}
    >
      <div className="thread-body">
        {hasMore && (
          <div className="thread-history">{loadingOlder ? 'Загружаем…' : ''}</div>
        )}

        {messages.map((message, index) => {
          const newDay =
            index === 0 || !isSameDay(messages[index - 1]!.createdAt, message.createdAt);

          // The assistant's memory boundary, drawn instead of a bubble. It is a
          // real row, so it carries data-message-id like any other — unlike the
          // day chip below, which is derived and would become an anchor with no
          // id to restore.
          if (message.aiNotice === 'reset') {
            return (
              <div className="thread-reset" data-message-id={message.id} key={message.id}>
                <span className="thread-reset-rule" />
                <span className="thread-reset-label">
                  <CircleSlash2 size={14} />
                  {/* Merged rather than stacked: two centred labels one under the
                      other read as two separate breaks in the conversation, and
                      on the day a разговор is closed there is only one. */}
                  {newDay
                    ? `Новый разговор · ${formatDaySeparator(message.createdAt)}`
                    : 'Новый разговор'}
                </span>
                <span className="thread-reset-rule" />
                {index === lastResetIndex && (
                  <span className="thread-reset-note">бот не помнит, что выше</span>
                )}
              </div>
            );
          }

          // A refusal, drawn as a plate across the thread rather than as one of
          // the bot's bubbles: it is not a turn in the conversation, and a reply
          // arrow or a reaction on «кончился лимит» would be nonsense.
          if (message.aiNotice === 'quota' || message.aiNotice === 'failed') {
            return <NoticePlate key={message.id} message={message} />;
          }

          return (
          <Fragment key={message.id}>
            {/* Derived from the message above rather than stored, which is what
                makes a page of history sort itself out — a separator whose day
                is no longer new simply stops being rendered. */}
            {newDay && (
              <div className="thread-day">
                <span>{formatDaySeparator(message.createdAt)}</span>
              </div>
            )}
            <Message
              message={message}
              cold={index < lastResetIndex}
              onRedraw={onRedraw}
              senderName={startsRun(messages, index, showSenders) ? message.senderName : null}
              senderAvatarId={message.senderAvatarId}
              showTicks={showTicks}
              onOpenImage={onOpenImage}
              onHold={onHold}
              picked={picked?.id === message.id ? picked.span : null}
              onPick={onPick}
              onPickCopy={onPickCopy}
              onPickQuote={onPickQuote}
              onReply={onReply}
              onJumpTo={jumpTo}
              flashing={message.id === flash?.id}
              highlight={highlight}
              playing={message.id === playingId}
              expanded={message.id === expandedId}
              onTogglePlay={togglePlay}
              onToggleExpand={toggleExpand}
              canTranscribe={canTranscribe}
              onTranscribe={(item) => onTranscribe(item.id)}
              onEnded={advance}
              onRetry={onRetry}
              dimmed={message.id === editingId}
              justNow={
                message.id === last?.id && lastIsLive && now - message.createdAt < JUST_NOW_MS
              }
            />
          </Fragment>
          );
        })}
      </div>
    </div>
  );
}
